import { Injectable } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { formatString } from '../../utils/string-utils';

import { BaseMap, MapControl, LengthUnit, ArealUnit, ProjectionUnit, SearchPredicate } from '../../models/map.interface';

import { loadModules, setDefaultOptions } from 'esri-loader';
import { XMLParser } from 'fast-xml-parser';

import cloneDeep from 'lodash/cloneDeep';

declare let Terraformer: any; // terraformer-wkt-parser.js, https://github.com/esri/terraformer-wkt-parser
declare let geohash: any;     // geohash.js


@Injectable({
	providedIn: 'root'
})
export class MapService {

	mapServiceEvent: EventEmitter<{ eventType: string, data?: any; timestamp: Date }> = new EventEmitter();

	esriConfig!: any;
	basemapConfigs: BaseMap[] = [];
	localGraphicsLibrary: { [key: string]: any } = {};
	mapControl!: MapControl;

	constructor(
		private configService: ConfigService,
		private userService: UserService,
		private http: HttpClient
	) {
		// Configure the global esri-loader
		// For options, see https://github.com/Esri/esri-loader?tab=readme-ov-file#configuring-esri-loader
		setDefaultOptions({
			version: this.configService.getConfig().version.arcgisVersion,  // Eg: 'https://js.arcgis.com/4.29/'
			css: true
		});

		this.loadEsriConfig();

		this.basemapConfigs = this.configService.getConfig()?.esri?.basemaps || [];

		this.mapControl = {
			overview: this.configService.getConfig()?.esri?.overview ?? false,
			maptips: true,
			highlight: false,
			lengthUnit: this.configService.getConfig().lengthUnit ?? "meters",
			arealUnit: "acres", //"UNIT_ACRES",
			projectionUnit: this.configService.getConfig()?.esri?.defaultProjection ?? "0",
			baseMapUnit: '',
			location: "address",
			locate: {},
			geometrySearchPredicate: this.configService.getConfig()?.esri?.searchPredicate ?? "Intersects",
			filmstripViewEnabled: this.configService.getConfig()?.esri?.filmstripViewEnabled ?? false
		};
		this.refreshMapControlSettingsForUser();

		this.loadGraphicsLibrary();
	}

	private async loadEsriConfig(): Promise<any> {
		try {
			const [config] = await loadModules(['esri/config']);

			if (this.configService.getConfig().esri.apiKey) {
				config.apiKey = this.configService.getConfig().esri.apiKey;
			}

			if (this.configService.getConfig().esri.corsEnabledServers) {
				for (let i = 0; i < this.configService.getConfig().esri.corsEnabledServers.length; i++) {
					config.request.trustedServers.push(this.configService.getConfig().esri.corsEnabledServers[i]);
				}
			}

			this.esriConfig = config;
		} catch (err) {
			console.error("ArcGIS API load error: ", err)
			return undefined;
		}
	}

	getMapControl(): MapControl {
		return cloneDeep(this.mapControl);
	}

	private refreshMapControlSettingsForUser(): void {
		let userData = this.userService.getLocalStorageData();
		if (userData.mapControl && Object.keys(userData.mapControl).length > 0) {
			this.mapControl = userData.mapControl;
		}
	}

	updateMapControlSettings(settings: MapControl): void {
		let userData = this.userService.getLocalStorageData();
		userData.mapControl = settings;
		this.userService.setLocalStorageData(userData);
		this.mapControl = settings;
	}

	// Used when map is detached
	message_broadcast(message: any): void {
		localStorage.setItem('map_message', JSON.stringify(message));
		localStorage.removeItem('map_message');
	}

	loadBaseMap(mapView: any, baseMapName?: string): void {
		baseMapName = (baseMapName || this.configService.getConfig().esri.defaultBasemap);

		let baseMap = this.basemapConfigs.find((x: any) => {
			return x.name === baseMapName;
		});

		baseMap = (baseMap || this.basemapConfigs[0]);

		if (baseMap.layer) {
			mapView.map.basemap = baseMap.layer;
		} else {
			mapView.map.basemap = baseMap.name;
		}

		this.mapControl.baseMapUnit = baseMap.name;
	}

	async openInMap(record: any, mapDetached: boolean = false): Promise<any> {
		if (!record) { return; }

		let geo = record.geo;
		if (!geo) { return; }

		this.mapServiceEvent.emit({
			eventType: 'openRecordInMap',
			data: {},
			timestamp: new Date(),
		});

		const [
			Point,
			Polyline,
			Multipoint,
			Polygon,
			SpatialReference,
			geometryService,
			AreasAndLengthsParameters,
			LengthsParameters,
			webMercatorUtils
		] = await loadModules([
			"esri/geometry/Point",
			"esri/geometry/Polyline",
			"esri/geometry/Multipoint",
			"esri/geometry/Polygon",
			"esri/geometry/SpatialReference",
			"esri/rest/geometryService",
			"esri/rest/support/AreasAndLengthsParameters",
			"esri/rest/support/LengthsParameters",
			"esri/geometry/support/webMercatorUtils"
		]);

		// Incoming geo is a WKT string
		let mpgeo = this.parseMultipointGeometry(geo);
		// Parse the WKT to a geometry. WKT points are in geographic coordinates
		let primitive = Terraformer.WKT.parse(mpgeo);
		let newGeom = Terraformer.ArcGIS.convert(primitive);
		let p = null;
		if (primitive instanceof Terraformer.LineString || primitive instanceof Terraformer.MultiLineString) {
			p = new Polyline(newGeom.paths);
		} else if (primitive instanceof Terraformer.Point) {
			p = new Point(newGeom);
		} else if (primitive instanceof Terraformer.MultiPoint) {
			try {
				p = new Multipoint(new SpatialReference({ wkid: 4326 }));
				p.points = newGeom.points;
			} catch (err) {
				// skip
			}
		} else if (primitive instanceof Terraformer.Polygon || primitive instanceof Terraformer.MultiPolygon) {
			p = new Polygon(newGeom.rings);
		} else {
			// fallback on Point
			p = new Point(newGeom.rings);
		}

		geometryService.simplify(this.configService.getConfig().esri.geometryService, [p]).then(
			(results: any) => {
				let data = {
					uuid: record.id,
					geom: results[0],
					update: true,
					extent: undefined
				};
				if (mapDetached) {
					data.extent = results[0].extent;
					this.message_broadcast({ 'action': 'zoomToGeo', 'data': data });
				} else {
					this.mapServiceEvent.emit({
						eventType: 'Zoom&SelectRecord',
						data: data,
						timestamp: new Date(),
					});
				}

				this.mapServiceEvent.emit({
					eventType: 'updateMapRecordPreview',
					data: {
						uuid: record.id,
						forceZoom: true
					},
					timestamp: new Date(),
				});
			},
			(err: any) => {
				console.error("Error simplifying geometry: ", err);
			}
		);
	}

	/* This expects an array of x,y coordinates, not a Geometry */
	async centerAndZoomMap(mapView: any, coordinates: string[], zoom: number): Promise<any> {
		try {
			const [Point] = await loadModules(['esri/geometry/Point']);
			let targetPoint = new Point({
				longitude: coordinates[0], // x
				latitude: coordinates[1]   // y
			});
			mapView.goTo({
				target: targetPoint,
				zoom: zoom
			}).then(
				(data: any) => {
					console.log("Map centered on the point with zoom level %s", zoom);
				},
				(err: any) => {
					console.error("Error centering view:", err);
				}
			);
		} catch (err) {
			console.error("ArcGIS API load error: ", err)
			return undefined;
		}
	}

	async getArea(polygon: any): Promise<any> {
		try {
			const [geometryEngine] = await loadModules(['esri/geometry/geometryEngine']);
			// Call the geometry engine to get the area
			// Possible UNIT values: "acres" |"ares" |"hectares" |"square-feet" |"square-meters" |"square-yards" |"square-kilometers" |"square-miles" | "square-inches"
			let geodesicArea = geometryEngine.geodesicArea(polygon, this.mapControl.arealUnit); // curved // TODO base this unit on $scope.mapControl.arealUnit
			let planarArea = geometryEngine.planarArea(polygon, this.mapControl.arealUnit); // flat       // TODO base this unit on $scope.mapControl.arealUnit 
			console.log("GeodesicArea: %s, PlanarArea: %s", geodesicArea, planarArea);
			let areas = {
				geodesicArea: geodesicArea,
				planarArea: planarArea
			};
			return areas;
		} catch (err) {
			console.error("ArcGIS API load error: ", err)
			return undefined;
		}
	}

	async getLength(line: any): Promise<any> {
		try {
			const [geometryEngine] = await loadModules(['esri/geometry/geometryEngine']);
			// Call the geometry engine to get the lengths
			// Possible UNIT values: "meters" | "feet" |"kilometers" |"miles" |"nautical-miles" |"yards" 
			let length = geometryEngine.geodesicLength(line, this.mapControl.lengthUnit);
			console.log("Length:", length);
			return length;
		} catch (err) {
			console.error("ArcGIS API load error: ", err)
			return undefined;
		}
	}

	getLengthUnits(): LengthUnit[] {
		return [{
			value: "UNIT_FOOT",
			engineUnit: "feet",
			name: "Feet",
			toMeter: 0.3048
		}, {
			value: "UNIT_STATUTE_MILE",
			engineUnit: "miles",
			name: "Miles",
			toMeter: 1609.34
		}, {
			value: "UNIT_METER",
			engineUnit: "meters",
			name: "Meters",
			toMeter: 1
		}, {
			value: "UNIT_KILOMETER",
			engineUnit: "kilometers",
			name: "Kilometers",
			toMeter: 1000

		}];
	}

	getProjectionUnits(): ProjectionUnit[] {
		// Example:		
		//		[
		//			{
		//				"name": "WGS 84",
		//				"value": "0"
		//              "projection": "_some proj4j string_"
		//			}
		//		]
		return cloneDeep(this.configService.getConfig().esri.projections);
	}

	getArealUnits(): ArealUnit[] {
		return [{
			value: "UNIT_ACRES",
			apiValue: "acres",
			name: "Acres"
		}, {
			value: "UNIT_HECTARES",
			apiValue: "hectares",
			name: "Hectares"
		}, {
			value: "UNIT_SQUARE_FEET",
			apiValue: "square-feet",
			name: "Square Feet"
		}, {
			value: "UNIT_SQUARE_METERS",
			apiValue: "square-meters",
			name: "Square Meters"
		}, {
			value: "UNIT_SQUARE_MILES",
			apiValue: "square-miles",
			name: "Square Miles"
		}];
	}

	getSearchPredicates(): SearchPredicate[] {
		return [{
			name: "Select records within shape",
			value: "IsWithin"
		},
		{
			name: "Select records that intersect shape",
			value: "Intersects"
		},
		{
			name: "Select records whose boundary contains shape",
			value: "Contains"
		}];
	}

	parseMultipointGeometry(geo: string | null | undefined): string | null | undefined {
		if (!geo) return geo;
		try {
			if (geo.includes('POINT')) {
				let multi = geo.split(";");
				if (multi.length > 1) {
					let points = [];
					for (let i = 0; i < multi.length; i++) {
						points.push(multi[i].replace("POINT", ""));
					}
					geo = "MULTIPOINT(" + points.join(",") + ")";
				}
			} else if (geo.includes('POLYGON')) {
				let multi = geo.split(";");
				if (multi.length > 1) {
					let polygons = [];
					for (let i = 0; i < multi.length; i++) {
						polygons.push(multi[i].replace("POLYGON", ""));
					}
					geo = "MULTIPOLYGON(" + polygons.join(",") + ")";
				}
			} else if (geo.includes('LINESTRING')) {
				let multi = geo.split(";");
				if (multi.length > 1) {
					let lines = [];
					for (let i = 0; i < multi.length; i++) {
						lines.push(multi[i].replace("LINESTRING", ""));
					}
					geo = "MULTILINESTRING(" + lines.join(",") + ")";
				}
			}
		} catch (err) {
			// do nothing
		}

		return geo;
	}

	async loadGraphicsLibrary(): Promise<any> {
		const [SimpleMarkerSymbol, SimpleLineSymbol, SimpleFillSymbol, PictureMarkerSymbol, TextSymbol, Color, Font] =
			await loadModules([
				'esri/symbols/SimpleMarkerSymbol',
				'esri/symbols/SimpleLineSymbol',
				'esri/symbols/SimpleFillSymbol',
				'esri/symbols/PictureMarkerSymbol',
				'esri/symbols/TextSymbol',
				'esri/Color',
				'esri/symbols/Font'
			]);
		let glib: { [key: string]: any } = {};

		let defaultSymbol = new SimpleLineSymbol({
			color: new Color("#FF0000"),
			width: 4,
			style: "solid"
		});
		glib['default'] = defaultSymbol;


		// Lines
		let lineSymbol = new SimpleLineSymbol({
			style: "solid",
			color: new Color([0, 0, 0]),
			width: 5
		});
		lineSymbol.cap = "round";
		lineSymbol.join = "miter";
		lineSymbol.miterLimit = 5;
		glib["line"] = lineSymbol;

		let lineSymbolBefore = lineSymbol.clone();
		lineSymbolBefore.color = new Color([0, 0, 255]);
		glib["line-before"] = lineSymbolBefore;

		let hashSymbol = defaultSymbol.clone();
		hashSymbol.color = new Color('#4c3100');
		hashSymbol.width = 2;
		glib["hash"] = hashSymbol;


		// Fills
		let fillSymbol = new SimpleLineSymbol({
			color: new Color("#00FF00"),
			width: 4,
			style: "solid"
		});
		glib['fill'] = fillSymbol;

		let fillSymbolBefore = fillSymbol.clone();
		fillSymbolBefore.color = new Color("#00FFFF");
		glib['fill-before'] = fillSymbolBefore;

		let aoiOutline = new SimpleLineSymbol({
			color: new Color("#FF0000"),
			width: 4,
			style: "solid"
		});
		let aoiSymbol = new SimpleFillSymbol({
			color: null,
			outline: aoiOutline
		});
		glib['aoi'] = aoiSymbol;

		let aoiFillSymbol = new SimpleFillSymbol({
			style: "solid",
			color: new Color([232, 104, 80, 0.25]),
			outline: new SimpleLineSymbol({
				style: "solid",
				color: new Color([232, 104, 80]),
				width: 4
			})
		});
		glib['aoi-fill'] = aoiFillSymbol;

		let locateFillSymbol = aoiFillSymbol.clone();
		locateFillSymbol.color = new Color([255, 170, 0, 0.25]);
		glib['locate-fill'] = locateFillSymbol;

		let polyWfsSymbol = fillSymbol.clone();
		polyWfsSymbol.color = new Color("#9932CC");
		polyWfsSymbol.width = 3;
		glib['poly-wfs'] = polyWfsSymbol;

		let polyWfsSelectedSymbol = fillSymbol.clone();
		polyWfsSelectedSymbol.color = new Color("#FF4500");
		polyWfsSelectedSymbol.width = 3;
		glib['poly-wfs-selected'] = polyWfsSelectedSymbol;


		// Picture Markers
		let pictureMarkerSymbol = new PictureMarkerSymbol({
			url: "",
			height: 24,
			width: 24
		});

		let markerSymbol = pictureMarkerSymbol.clone();
		markerSymbol.url = "https://maps.google.com/mapfiles/kml/pushpin/ltblu-pushpin.png";
		glib['marker'] = markerSymbol;

		// the images folder contains additional fill images, other options: sand.png, swamp.png or stiple.pngh				
		let markerSymbolBefore = pictureMarkerSymbol.clone();
		markerSymbolBefore.url = "/app/aw-ui-angular/assets/img/blackStar.webp";
		glib['marker-before'] = markerSymbolBefore;

		let locatePointSymbol = pictureMarkerSymbol.clone();
		locatePointSymbol.url = "https://maps.google.com/mapfiles/kml/pushpin/ylw-pushpin.png";
		glib['locate-point'] = locatePointSymbol;


		// Simple Markers
		let singleSymbol = new SimpleMarkerSymbol({
			color: [0, 0, 128, 1],
			size: 12,
			angle: 0,
			xoffset: 0,
			yoffset: 0,
			style: "circle",
			outline: {
				color: [0, 0, 128, 1],
				width: 1
			}
		});
		glib['single'] = singleSymbol;

		let singleSymbolSelected = singleSymbol.clone();
		singleSymbolSelected.color = [255, 0, 0, 1];
		singleSymbolSelected.outline.color = [255, 0, 0, 1];
		glib['single-selected'] = singleSymbolSelected;

		let subSingleSymbol = singleSymbol.clone();
		subSingleSymbol.color = [0, 200, 16, 1];
		subSingleSymbol.size = 10;
		subSingleSymbol.outline.color = [0, 200, 16, 1];
		glib['sub-single'] = subSingleSymbol;

		let subSingleSymbolSelected = singleSymbol.clone();
		subSingleSymbolSelected.color = [0, 200, 16, 1];
		subSingleSymbolSelected.size = 13;
		subSingleSymbolSelected.outline.color = [0, 0, 0, 1];
		subSingleSymbolSelected.outline.width = 2;
		glib['sub-single-selected'] = subSingleSymbolSelected;


		// Text
		let textSymbol = new TextSymbol({
			text: "",
			color: new Color([255, 255, 255]),
			font: new Font({
				size: "12pt",
				weight: "bold",
				family: "sans-serif"
			}),
			haloColor: new Color([0, 0, 0, 0.5]),
			haloSize: "1px",
			angle: 0,
			horizontalAlignment: "center",
			xoffset: 0,
			yoffset: -2
		});
		glib['text'] = textSymbol;


		// Clusters			
		let clusterSymbol = new SimpleMarkerSymbol({
			color: [0, 0, 128, 0.5],
			size: 12,
			angle: 0,
			xoffset: 0,
			yoffset: 0,
			style: "circle",
			outline: {
				color: [0, 0, 128, 1],
				width: 1
			}
		});
		glib['cluster'] = clusterSymbol;

		let clusterSelectedSymbol = clusterSymbol.clone();
		clusterSelectedSymbol.color = [255, 0, 128, 0.5];
		clusterSelectedSymbol.outline.color = [255, 0, 128, 1];
		glib['cluster-selected'] = clusterSelectedSymbol;

		this.localGraphicsLibrary = glib;
	}

	getSymbol(type: string, size?: number, text?: string): any {
		let template = this.localGraphicsLibrary[type] || this.localGraphicsLibrary['default'];
		let symbol = template.clone();
		if (size) symbol.size = (size.toString().length * 5) + 10;
		if (text) symbol.text = String(text);
		return symbol;
	}

	async uploadShapefile(file: File): Promise<any> {
		const formData = new FormData();
		formData.append('productFile', file, file.name);

		let url = '/aw-server/rest/location/import';

		try {
			return await firstValueFrom(this.http.post<any>(url, formData));
		} catch (error) {
			return Promise.reject(error);
		}

		// Orig: 
		//		let deferred = $q.all(null);
		//		let cancelled = $q.defer();
		//		$rootScope.$on("cancelShapefileUpload", function () {
		//		    cancelled.resolve();
		//		});
		//
		//		deferred = deferred.then(function () {
		//		    let formData = new FormData();
		//		    formData.append("productFile", file, file.name);
		//		    return $http({
		//		        url: "/aw-server/rest/location/import",
		//		        method: 'POST',
		//		        data: formData,
		//		        transformResponse: undefined,
		//		        headers: { 'Content-Type': undefined },
		//		        transformRequest: angular.identity,
		//		        timeout: cancelled.promise
		//		    }).then(function (response) {
		//		        let data = JSON.parse(response.data);
		//		        return data;
		//		    }, function (response) {
		//		        return response;
		//		    });
		//		});
		//		return deferred;
	}

	async addLocation(payload: any): Promise<any> {
		let url = this.configService.getConfig().locations.addLocationURL;

		try {
			return await firstValueFrom(this.http.post<any>(url, payload));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async deleteLocation(id: any): Promise<any> {
		let url = formatString(this.configService.getConfig().locations.deleteLocationURL, id);

		try {
			return await firstValueFrom(this.http.post<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updateLocation(id: any, payload: any = {}): Promise<any> {
		let url = formatString(this.configService.getConfig().locations.updateLocationURL, id);

		try {
			return await firstValueFrom(this.http.put<any>(url, payload));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getSavedLocations(page: number = 1, paging: any = {}, constraints: any = {}): Promise<any> {
		let url = this.configService.getConfig().locations.getLocationsURL;
		url = url + "?page=" + page + "&size=" + paging.elementsPerPage;
		url = url + "&sort=" + paging.sorting.sort + "&constraints=" + encodeURIComponent(JSON.stringify(constraints));

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response?.content) {
				return response;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async georeferenceImage(id: number | string, page: number = 0, points: any[]): Promise<any> {
		if (points.length < 3) {
			return Promise.reject("Accurate georeferencing requires a minimum of 3 control points.");
		}

		let url = formatString(this.configService.getConfig().document.documentGeoreference, id, page);

		try {
			return await firstValueFrom(this.http.post<any>(url, points));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getProjectedInfo(id: number | string, page: number): Promise<string> {
		let url = formatString(this.configService.getConfig().document.projectionUrl, id, page);

		try {
			return await firstValueFrom(this.http.get(url, { responseType: 'text' }));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getFeatures(url: string): Promise<any> {
		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getFeaturesCount(url: string): Promise<any> {
		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async recordPageGeometries(id: number | string): Promise<any> {
		let url = formatString(this.configService.getConfig().document.pageGeometriesURL, id);

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getKML(url: string): Promise<any> {
		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getKMLLayers(url: string): Promise<any> {
		const [
			SpatialReference
		] = await loadModules([
			'esri/geometry/SpatialReference'
		]);

		try {
			let response = await this.getKML(url);

			let kmlContents = encodeURIComponent(response); // encode the kml string
			let formData = new FormData();
			formData.append("url", "");
			formData.append("kmlString", kmlContents);
			formData.append("outSR", JSON.stringify(new SpatialReference({ wkid: 4326 })))
			formData.append("method", "simple");

			// Original:
			//              method: "POST",
			//              data: formData,
			//              url: "https://utility.arcgis.com/sharing/kml",
			//              headers: {
			//                  'Content-Type': undefined,
			//                  'X-XSRF-TOKEN': undefined,
			//                  'X-Requested-With': undefined
			//              }			
			let utilityURL = 'https://utility.arcgis.com/sharing/kml';
			return await firstValueFrom(this.http.post<any>(utilityURL, formData));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async samePoint(a: any, b: any): Promise<boolean> {
		if (a == null || b == null) {
			return false;
		}

		if (a.uuid != null && b.uuid != null && b.uuid == a.uuid) {
			return true;
		}

		if (a.geoHash != null && a.geoHash == b.geoHash) {
			return true;
		}

		if (a.geoHash != null && b.uuid != null && b.geo) {
			// compute geohash of selected map record
			if (b.geo.type == "point") {
				if (b.geo && b.geo.x && b.geo.y) {
					let hash = geohash.encode(b.geo.y, b.geo.x, 16);
					if (a.geoHash.includes(hash) || hash.includes(a.geoHash)) {
						return true;
					}
				}
			} else if (b.geo.type == "polyline") {
				const [
					Point,
					SpatialReference,
					geometryEngine
				] = await loadModules([
					"esri/geometry/Point",
					"esri/geometry/SpatialReference",
					"esri/geometry/geometryEngine"
				]);

				let geo = geohash.decode(a.geoHash);
				let point = new Point(geo.latitude, geo.longitude, new SpatialReference({ wkid: 4326 }));
				let intersects = geometryEngine.intersects(point, b.geo);
				return intersects;

			} else if (b.geo.type == "polygon") {
				let center = b.geo.getCentroid();
				let hash = geohash.encode(center.y, center.x, 16);
				if (a.geoHash.includes(hash) || hash.includes(a.geoHash)) {
					return true;
				}
			}
		}
		return false;
	}

	async pointToExtent(view: any, point: any, toleranceInPixel: any): Promise<any> {
		const [Extent] = await loadModules(["esri/geometry/Extent"]);
		// calculate map coords for tolerance in pixel
		const pixelWidth = view.extent.width / view.width;

		// calculate tolerance in map coordinates
		const toleranceInMapCoords = toleranceInPixel * pixelWidth;

		// calculate & return computed extent
		let computedExtent = new Extent({
			xmin: point.x - toleranceInMapCoords,
			ymin: point.y - toleranceInMapCoords,
			xmax: point.x + toleranceInMapCoords,
			ymax: point.y + toleranceInMapCoords,
			spatialReference: view.spatialReference
		});

		return computedExtent;
	}

	async getGeometryFromWKT(mpgeo: any): Promise<any> {
		const [
			Point,
			Polyline,
			Multipoint,
			Polygon,
			Circle,
			SpatialReference,
			webMercatorUtils
		] = await loadModules([
			"esri/geometry/Point",
			"esri/geometry/Polyline",
			"esri/geometry/Multipoint",
			"esri/geometry/Polygon",
			"esri/geometry/Circle",
			"esri/geometry/SpatialReference",
			"esri/geometry/support/webMercatorUtils"
		]);

		try {
			let primitive = Terraformer.WKT.parse(mpgeo);
			let newGeom = Terraformer.ArcGIS.convert(primitive);
			let p = null;
			if (primitive instanceof Terraformer.LineString || primitive instanceof Terraformer.MultiLineString) {
				p = new Polyline(newGeom.paths);
			} else if (primitive instanceof Terraformer.Point) {
				p = new Point(newGeom);
			} else if (primitive instanceof Terraformer.MultiPoint) {
				try {
					p = new Multipoint(new SpatialReference({ wkid: 4326 }));
					p.points = newGeom.points;
				} catch (err) {
					console.error("Error creating multipoint geometry: ", err);
				}
			} else if (primitive instanceof Terraformer.Polygon || primitive instanceof Terraformer.MultiPolygon) {
				p = new Polygon(newGeom.rings);
			} else {
				// fallback on Point
				p = new Point(newGeom.rings);
			}
			return p;
		} catch (err) {
			console.error("Error creating geometry: ", err);
			Promise.reject("Invalid geometry");
		}
	}

	async searchByWKTLocation(geo: string): Promise<any> {
		const [
			Point,
			Circle,
			webMercatorUtils
		] = await loadModules([
			"esri/geometry/Point",
			"esri/geometry/Circle",
			"esri/geometry/support/webMercatorUtils"
		]);

		// Incoming geo is a WKT string
		// Check if a point
		let primitive = null;
		try {
			primitive = Terraformer.WKT.parse(geo);

			// If a point, we need to create a search radius
			if (primitive instanceof Terraformer.Point) {
				let y = primitive.coordinates[1];
				let x = primitive.coordinates[0];
				let d = 1000 * 10.32; // In meters
				let point = new Point({
					x: x,
					y: y,
					spatialReference: { wkid: 4326 } // Geographic coordinates
				});
				let geom = new Circle({
					"center": point,
					"radius": d,
					"radiusUnit": "meters",
					geodesic: true
				});

				for (let c = 0; c < geom.rings[0].length; c++) {
					geom.rings[0][c] = webMercatorUtils.lngLatToXY(geom.rings[0][c][0], geom.rings[0][c][1]);
				}

				geom.spatialReference = { wkid: 102100 }; // Web Mercator
				geom._srsShape = "circle";
				this.mapServiceEvent.emit({
					eventType: 'AddNewAOI',
					data: { geometry: geom, forceZoom: true },
					timestamp: new Date(),
				});
				return;
			}
		} catch (notapoint) { }

		// Just send the WKT
		//		this.mapServiceEvent.emit({
		//			eventType: 'RedrawMapAOI',
		//			data: { aoi: geo, forceZoom: true },
		//			timestamp: new Date(),
		//		});
		this.goToWKTLocation(geo, true);

		this.mapServiceEvent.emit({
			eventType: 'updateAOI',
			data: { aoi: geo },
			timestamp: new Date(),
		});
	}

	async goToWKTLocation(geo: string, forceZoom: boolean = false): Promise<any> {
		const [
			Point,
			Circle,
			webMercatorUtils
		] = await loadModules([
			"esri/geometry/Point",
			"esri/geometry/Circle",
			"esri/geometry/support/webMercatorUtils"
		]);

		// Incoming geo is a WKT string
		if (geo.indexOf("CIRCLE") == 0) {
			let beg = geo.indexOf("(");
			let end = geo.indexOf(")");
			let sub = geo.substring(beg + 1, end);
			let comp = sub.split(/[\s,]+/);
			let y = comp[0];
			let x = comp[1];
			let d = comp[2].replace("d=", "");
			let point = new Point({
				x: x,
				y: y,
				spatialReference: { wkid: 4326 } // Geographic coordinates
			});
			let geom = new Circle({
				"center": point,
				"radius": d,
				"radiusUnit": "meters",
				geodesic: true
			});

			for (let c = 0; c < geom.rings[0].length; c++) {
				geom.rings[0][c] = webMercatorUtils.lngLatToXY(geom.rings[0][c][0], geom.rings[0][c][1]);
			}
			geom.spatialReference = { wkid: 102100 }; // Web Mercator

			this.mapServiceEvent.emit({
				eventType: 'ZoomIdentify',
				data: {
					geometry: geom
				},
				timestamp: new Date(),
			});
		}
		else {
			let mpgeo = this.parseMultipointGeometry(geo);
			this.getGeometryFromWKT(mpgeo).then(
				(p: any) => {
					this.mapServiceEvent.emit({
						eventType: 'ZoomIdentify',
						data: {
							geometry: p
						},
						timestamp: new Date(),
					});
				},
				(err: any) => {
					console.error("Failed to get geometry: ", err);
					Promise.reject("Cannot zoom to location. Invalid geometry.");
				}
			);
		}
	}

	/* 
	 * Creates a GeoJSONLayer from the URL and data. 
	 */
	async loadFeatureMapLayer(url: string, data: any, fullRecordMetadata: any = {}): Promise<any> {
		const [
			GeoJSONLayer,
			Field,
			PopupTemplate
		] = await loadModules([
			"esri/layers/GeoJSONLayer",
			"esri/layers/support/Field",
			"esri/PopupTemplate"
		]);

		let featureLayer = new GeoJSONLayer({
			url: url
		});

		// Determine if this record matches a symbology config
		let symbologyObj = null;
		let domain = this.userService.user.domain || 'GLOBAL';
		if (this.configService.getConfig().esri.symbology && this.configService.getConfig().esri.symbology[domain]) {
			for (let i = 0; i < this.configService.getConfig().esri.symbology[domain].length; i++) {
				let symbolObject = this.configService.getConfig().esri.symbology[domain][i];
				if (symbolObject.metadataFilter && symbolObject.metadataFilter.key) {
					if (fullRecordMetadata[symbolObject.metadataFilter.key]) {
						let reg = new RegExp(symbolObject.metadataFilter.value);
						let match = fullRecordMetadata[symbolObject.metadataFilter.key].match(reg);
						if (match && match.length > 0) {
							symbologyObj = symbolObject;
							break;
						}
					}
				}
			}
		}

		// If no symbology, the GeoJSONLayer will default to using a renderer
		// appropriate for the types found in the JSON
		if (symbologyObj) {
			featureLayer.renderer = symbologyObj;
		}

		// Display a info popup when clicking on a feature
		// Tell the layer what fields to expect in the popup
		const createFieldsFromJSON = (data: any) => {
			// Content expects an array of fieldInfos
			// See https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#content
			// Example:
			//              content: [{
			//                // Pass in the fields to display
			//                type: "fields",
			//                fieldInfos: [{
			//                  fieldName: "title",
			//                  label: "Title"
			//                }, {
			//                  fieldName: "mag",
			//                  label: "Magnitude"
			//               }]
			//              }]  

			let fields = [{
				type: "fields",
				fieldInfos: [] as { fieldName: string; label: string }[]
			}];
			if (data && data.features && data.features.length > 0) {
				let properties = data.features[0].properties;
				for (let key in properties) {
					if (properties.hasOwnProperty(key)) {
						if ('geometry' === key) continue;
						fields[0].fieldInfos.push({ fieldName: key, label: key });
					}
				}
			}
			return fields;
		};
		let infoContent = createFieldsFromJSON(data);
		let popupTemplate = new PopupTemplate({
			title: "GeoJSON Data",
			content: infoContent
		});
		featureLayer.popupTemplate = popupTemplate;

		return featureLayer;
	}

	/**
	 * Distance is unit agnostic.
	 * Assumes both extents are in the same unit of measurement.
	 */
	getExtentsDistance(extent1: any, extent2: any): number {
		let center1 = extent1.center;
		let center2 = extent2.center;

		let deltaX = center2.x - center1.x;
		let deltaY = center2.y - center1.y;
		let distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
		return distance;
	}

	prettyPrintExtent(extent: any): string {
		if (!extent) return "";
		return `
	     - XMin: ${extent.xmin.toFixed(5)}
	     - YMin: ${extent.ymin.toFixed(5)}
	     - XMax: ${extent.xmax.toFixed(5)}
	     - YMax: ${extent.ymax.toFixed(5)}
	     - Spatial Reference: ${extent.spatialReference.wkid}
	    `;
	}

	/**
	 * Equivalent to $.xml2json(data)
	 */
	getJSONfromXML(xml: string): any {
		const parser = new XMLParser();
		return parser.parse(xml);
	}

	/**
	 * Convenience method for triggering an Open in Map event broadcast.
	 */
	broadcastOpenMapEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'openRecordInMap',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering an AOI update event broadcast.
	 */
	broadcastUpdateAOIEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'updateAOI',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering an AOI redraw event broadcast.
	 */
	broadcastRedrawAOIEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'RedrawMapAOI',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering a Geometry Search Predicate update event broadcast.
	 */
	broadcastUpdateGeometrySearchPredicateEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'updateGeometrySearchPredicate',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering a Zoom and Select Record event broadcast.
	 */
	broadcastZoomSelectRecordEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'Zoom&SelectRecord',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering a Location Redraw event broadcast.
	 */
	broadcastRedrawLocationsEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'redrawLocations',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering a Clear AOI event broadcast.
	 */
	broadcastClearAOIEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'clearAOI',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering an Add AOI event broadcast.
	 */
	broadcastAddAOIEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'AddNewAOI',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering an Update AOI by Extent event broadcast.
	 */
	broadcastUpdateAOIByExtentEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'UpdateAOIByExtent',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering an Extent Tracking event broadcast.
	 */
	broadcastTrackExtentEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'trackExtentChanges',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering an Add Location Zoom event broadcast.
	 */
	broadcastAddLocationZoomEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'AddLocation&Zoom',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering a Filmstrip Result Updated event broadcast.
	 */
	broadcastFilmstripResultEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'filmstripResultUpdated',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering a Record Preview Update event broadcast.
	 */
	broadcastUpdatePreviewEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'updateMapRecordPreview',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering a Result - Zoom to Record event broadcast.
	 */
	broadcastResult_ZoomToEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'result:ZoomToRecord',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering a Result - Update Map Record Preview event broadcast.
	 */
	broadcastResult_UpdatePreviewEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'result:UpdateMapRecordPreview',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering a Result - Update Map Record Active Location event broadcast.
	 */
	broadcastResult_UpdateActiveLocationEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'result:UpdateMapRecordActiveLocation',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering a Map Ready event broadcast.
	 */
	broadcastMapReadyEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'mapReady',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering an Unselect Record event broadcast.
	 */
	broadcastUnselectRecordEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'unselectRecordMap',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering an Selected Geomegry Change event broadcast.
	 */
	broadcastSelectedGeomChangeEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'selectedGeomChanged',
			data: payload,
			timestamp: new Date(),
		});
	}

	/**
	 * Convenience method for triggering an Expand Record event broadcast.
	 */
	broadcastExpandRecordEvent(payload?: any): void {
		this.mapServiceEvent.emit({
			eventType: 'expandRecord',
			data: payload,
			timestamp: new Date(),
		});
	}
}
